import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import StudentSdebar from "./StudentSdebar";
import Header from "../Components/Header";

export default function AdminCreateMembership() {
  // ---------------- STATES ----------------
  const [membershipType, setMembershipType] = useState("basic");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [intendedLearner, setIntendedLearner] = useState("");
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [curriculumCourses, setCurriculumCourses] = useState([]);
  const [accessType, setAccessType] = useState("free");
  const [price, setPrice] = useState("");
  const [expiryType, setExpiryType] = useState("lifetime");
  const [expiryMonths, setExpiryMonths] = useState("");
  const [expiryYears, setExpiryYears] = useState("");
  const [status, setStatus] = useState("draft");
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [promoVideoUrl, setPromoVideoUrl] = useState("");
  const [membershipMessage, setMembershipMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [curriculumSearchQuery, setCurriculumSearchQuery] = useState("");
  const [pageLoading, setPageLoading] = useState(true); // For initial API loading
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showCurriculumDropdown, setShowCurriculumDropdown] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({
    basic: false,
    pro: false,
    enterprise: false,
    free: false,
    paid: false,
    lifetime: false,
    expiry: false,
    published: false,
    draft: false
  });

  const API_CREATE = "https://kaasbackend.vetrinahealthcare.com/lms/membership/create/";
  const API_COURSES = "https://kaasbackend.vetrinahealthcare.com/lms/courses/";

  // ---------------- THEME ----------------
  const theme = {
    primary: " #B8529E", // Purple
    secondary: "#F97316", // Light Purple
    accent: "#F97316", // Orange
    lightAccent: "#FB923C",
    background: "#F8FAFC",
    cardBg: "#FFFFFF",
    text: "#1F2937",
    lightText: "#6B7280",
    border: "#E5E7EB",
    error: "#EF4444",
    success: "#10B981"
  };

  // ---------------- STYLES ----------------
  const styles = {
    page: {
      minHeight: "100vh",
      background: theme.background,
      fontFamily: "'Inter', sans-serif",
      position: "relative"
    },
    container: {
      maxWidth: "1400px",
      margin: "0 auto",
      background: theme.cardBg,
      padding: "1.5rem",
      borderRadius: "1rem",
      boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
      '@media (minWidth: 768px)': {
        padding: "2rem",
      }
    },
    heading: {
      fontSize: "1.75rem",
      fontWeight: "800",
      color: theme.primary,
      marginBottom: "0.5rem",
      '@media (minWidth: 768px)': {
        fontSize: "2.5rem",
      }
    },
    sub: {
      color: theme.lightText,
      marginBottom: "2rem",
      fontSize: "0.875rem",
      '@media (minWidth: 768px)': {
        fontSize: "1rem",
      }
    },
    label: {
      fontSize: "0.875rem",
      fontWeight: "600",
      color: theme.primary,
      marginBottom: "0.5rem",
      display: "block",
    },
    input: {
      width: "100%",
      padding: "0.75rem 1rem",
      borderRadius: "0.75rem",
      border: `2px solid ${theme.border}`,
      marginBottom: "1rem",
      fontSize: "1rem",
      transition: "all 0.3s ease",
      background: theme.cardBg,
      '&:focus': {
        outline: 'none',
        borderColor: theme.primary,
        boxShadow: `0 0 0 3px ${theme.primary}20`,
      }
    },
    textarea: {
      width: "100%",
      padding: "0.75rem 1rem",
      borderRadius: "0.75rem",
      border: `2px solid ${theme.border}`,
      height: "120px",
      marginBottom: "1rem",
      fontSize: "1rem",
      resize: "vertical",
      transition: "all 0.3s ease",
      background: theme.cardBg,
      '&:focus': {
        outline: 'none',
        borderColor: theme.primary,
        boxShadow: `0 0 0 3px ${theme.primary}20`,
      }
    },
    btnChip: (active, loading) => ({
      padding: "0.75rem 1.5rem",
      borderRadius: "2rem",
      border: `2px solid ${active ? theme.accent : theme.lightAccent}`,
      background: active ? theme.accent : "transparent",
      color: active ? "white" : theme.accent,
      fontWeight: "600",
      cursor: loading ? "not-allowed" : "pointer",
      marginRight: "0.75rem",
      marginBottom: "0.5rem",
      transition: "all 0.3s ease",
      opacity: loading ? 0.7 : 1,
      position: "relative",
      minWidth: "100px",
      fontSize: "0.875rem",
      '@media (minWidth: 768px)': {
        fontSize: "1rem",
        marginBottom: "0",
      }
    }),
    section: {
      marginBottom: "2rem",
      padding: "1.5rem",
      background: theme.background,
      borderRadius: "1rem",
      border: `1px solid ${theme.border}`,
    },
    card: {
      background: theme.cardBg,
      padding: "1.25rem",
      borderRadius: "0.75rem",
      marginTop: "0.75rem",
      border: `2px solid ${theme.border}`,
      transition: "all 0.3s ease",
    },
    submit: {
      background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
      color: "white",
      padding: "1rem 2rem",
      border: "none",
      borderRadius: "0.75rem",
      cursor: loading ? "not-allowed" : "pointer",
      fontSize: "1rem",
      fontWeight: "700",
      width: "100%",
      maxWidth: "300px",
      boxShadow: "0 8px 25px rgba(109, 40, 217, 0.3)",
      transition: "all 0.3s ease",
      opacity: loading ? 0.7 : 1,
      position: "relative",
      '@media (minWidth: 768px)': {
        width: "220px",
      }
    },
    row: {
      display: "flex",
      flexDirection: "column",
      gap: "2rem",
      '@media (minWidth: 1024px)': {
        flexDirection: "row",
      }
    },
    col: {
      flex: "1",
      minWidth: "0",
    },
    loader: {
      display: "inline-block",
      width: "16px",
      height: "16px",
      border: "2px solid transparent",
      borderTop: "2px solid currentColor",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      marginRight: "0.5rem",
    },
    courseCard: (selected) => ({
      padding: "1.25rem",
      border: `2px solid ${selected ? theme.accent : theme.border}`,
      borderRadius: "0.75rem",
      marginBottom: "0.75rem",
      background: selected ? `${theme.accent}10` : theme.cardBg,
      cursor: "pointer",
      display: "flex",
      alignItems: "flex-start",
      gap: "1rem",
      transition: "all 0.3s ease",
    }),
    buttonGroup: {
      display: "flex",
      flexWrap: "wrap",
      gap: "0.5rem",
      alignItems: "center",
    },
    searchInput: {
      width: "100%",
      padding: "0.75rem 1rem",
      borderRadius: "0.75rem",
      border: `2px solid ${theme.border}`,
      marginBottom: "1rem",
      fontSize: "0.875rem",
      transition: "all 0.3s ease",
      background: theme.cardBg,
      '&:focus': {
        outline: 'none',
        borderColor: theme.primary,
        boxShadow: `0 0 0 3px ${theme.primary}20`,
      }
    },
    dropdownContainer: {
      maxHeight: "300px",
      overflowY: "auto",
      marginTop: "0.5rem",
      padding: "0.5rem",
      border: `1px solid ${theme.border}`,
      borderRadius: "0.5rem",
      background: theme.cardBg,
      position: "relative",
      zIndex: 10,
    },
    courseItem: {
      padding: "0.75rem",
      borderBottom: `1px solid ${theme.border}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
      '&:hover': {
        background: `${theme.primary}10`,
      },
      '&:last-child': {
        borderBottom: 'none',
      }
    },
    contentLoader: {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      background: "rgba(255, 255, 255, 0.8)",
      borderRadius: '1rem', // Match container border radius
    },
    submitLoader: {
      position: "absolute",
      left: "1rem",
      top: "50%",
      transform: "translateY(-50%)",
      width: "20px",
      height: "20px",
      border: "2px solid transparent",
      borderTop: "2px solid white",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    dropdownButton: {
      width: "100%",
      padding: "0.75rem 1rem",
      borderRadius: "0.75rem",
      border: `2px solid ${theme.border}`,
      background: theme.cardBg,
      color: theme.text,
      fontSize: "1rem",
      textAlign: "left",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1rem",
      transition: "all 0.3s ease",
      '&:hover': {
        borderColor: theme.primary,
      }
    },
    selectedCourses: {
      display: "flex",
      flexWrap: "wrap",
      gap: "0.5rem",
      marginBottom: "1rem",
    },
    selectedCourseTag: {
      background: `${theme.secondary}10`,
      color: theme.secondary,
      padding: "0.5rem 1rem",
      borderRadius: "2rem",
      fontSize: "0.875rem",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    removeButton: {
      background: "none",
      border: "none",
      color: theme.secondary,
      fontSize: "1.2rem",
      cursor: "pointer",
      padding: "0",
      width: "20px",
      height: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      '&:hover': {
        color: theme.error,
      }
    },
    mainContent: {
      position: "relative",
      marginTop: '60px',
      overflowY: 'auto',
      height: 'calc(100vh - 60px)',
      backgroundColor: '#F8F9FA',
      width: '100%',
      padding: sidebarExpanded ? '2rem' : '2rem 2rem 2rem 1rem',
      transition: 'padding 0.3s ease, margin-left 0.3s ease',
      boxSizing: 'border-box',
    }
  };

  // ---------------- Effects ----------------
  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowCourseDropdown(false);
        setShowCurriculumDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!coverImageFile) return;
    const reader = new FileReader();
    reader.onload = (e) => setCoverImagePreview(e.target.result);
    reader.readAsDataURL(coverImageFile);
  }, [coverImageFile]);

  // ---------------- API Functions ----------------
  const fetchCourses = async () => {
    try {
      setPageLoading(true);
      const token = localStorage.getItem("jwttoken");
      const response = await axios.get(API_COURSES, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.data && response.data.courses && Array.isArray(response.data.courses)) {
        const formattedCourses = response.data.courses.map(course => ({
          id: course.course_id,
          name: course.title || "Untitled Course",
          description: course.description || "No description available",
          categories: course.categories || [],
          instructor: course.instructor?.name || "Unknown Instructor",
          price: course.price || 0,
          rating: course.rating || 0,
          cover_img: course.cover_img
        }));
        setAvailableCourses(formattedCourses);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      // Fallback to mock data if API fails
      const mockCourses = [
        {
          id: 27,
          name: "VetZone",
          description: null,
          categories: ["Behavioural Training"],
          instructor: "Vetrina Healthcare Pvt. Ltd",
          price: 0.0,
          rating: 0.0
        },
        {
          id: 41,
          name: "HR Induction",
          description: "Details about company",
          categories: ["Resource Management"],
          instructor: "Vetrina Healthcare Pvt. Ltd",
          price: 0.0,
          rating: 0.0
        },
      ];
      setAvailableCourses(mockCourses);
    } finally {
      // Add a small delay to make the loader visible for a better UX
      setTimeout(() => setPageLoading(false), 500);
    }
  };

  // ---------------- Handlers ----------------
  const handleButtonClick = async (buttonType, action) => {
    setButtonLoading(prev => ({ ...prev, [buttonType]: true }));
    await new Promise(resolve => setTimeout(resolve, 300));
    action();
    setButtonLoading(prev => ({ ...prev, [buttonType]: false }));
  };

  const toggleSelectCourse = (id) => {
    setSelectedCourses((prev) => 
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleCurriculumCourse = (id) => {
    setCurriculumCourses((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const removeSelectedCourse = (id) => {
    setSelectedCourses(prev => prev.filter(courseId => courseId !== id));
  };

  const removeCurriculumCourse = (id) => {
    setCurriculumCourses(prev => prev.filter(courseId => courseId !== id));
  };

  const getCourseName = (id) => {
    const course = availableCourses.find(c => c.id === id);
    return course ? course.name : "Unknown Course";
  };

  const filterCourses = (courses, query) => {
    if (!query) return courses;
    return courses.filter(course =>
      course.name.toLowerCase().includes(query.toLowerCase()) ||
      (course.description && course.description.toLowerCase().includes(query.toLowerCase())) ||
      course.categories.some(cat => cat.toLowerCase().includes(query.toLowerCase()))
    );
  };

  // ---------------- Submit ----------------
  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    const payload = {
      membership_type: membershipType,
      title,
      description,
      intended_learner: intendedLearner,
      access_type: accessType,
      price: accessType === "paid" ? Number(price) : 0,
      expiry_type: expiryType,
      expiry_months:
        expiryType === "expiry"
          ? Number(expiryMonths || expiryYears * 12)
          : null,
      status,
      course_ids: selectedCourses,
      curriculum_course_ids: curriculumCourses,
      cover_image_base64: coverImagePreview || null,
      promo_video_url: promoVideoUrl || null,
      membership_message: membershipMessage || null,
    };

    try {
      const token = localStorage.getItem("jwtToken");
      await axios.post(API_CREATE, payload, {
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });
      alert("Membership Created Successfully!");
      // Reset form
      setTitle("");
      setDescription("");
      setIntendedLearner("");
      setSelectedCourses([]);
      setCurriculumCourses([]);
      setPrice("");
      setExpiryMonths("");
      setExpiryYears("");
      setCoverImageFile(null);
      setCoverImagePreview(null);
      setPromoVideoUrl("");
      setMembershipMessage("");
      setStatus("draft");
      setAccessType("free");
      setExpiryType("lifetime");
      setMembershipType("basic");
      setSearchQuery("");
      setCurriculumSearchQuery("");
      setShowCourseDropdown(false);
      setShowCurriculumDropdown(false);
    } catch (err)      {
      alert("Error creating membership. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ---------------- Loader Component ----------------
  const Loader = ({ size = "16px", color = "currentColor" }) => (
    <div style={{ 
      ...styles.loader, 
      width: size, 
      height: size,
      borderTopColor: color 
    }} />
  );

  // ---------------- UI ----------------
  return (
    <div style={styles.page}>
      <Header />
      <div className="wrapper" style={{ display: "flex" }}>
        <StudentSdebar 
          onToggle={setSidebarExpanded} 
          expandedWidth="280px"
          collapsedWidth="80px"
        />
        
        <motion.div
          className={`main-content ${sidebarExpanded ? "sidebar-expanded" : "sidebar-collapsed"}`}
          style={styles.mainContent}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {pageLoading ? (
            <div style={styles.contentLoader}>
              <Loader size="40px" color={theme.primary} />
              <p
                style={{
                  marginTop: "1rem",
                  color: theme.primary,
                  fontWeight: "600",
                  fontSize: "1.125rem"
                }}
              >
                Loading Content...
              </p>
            </div>
          ) : (
            <motion.div
              style={styles.container}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 style={styles.heading}>Create Membership</h1>
              <p style={styles.sub}>Create and publish memberships for learners</p>

              <form onSubmit={handleSubmit}>
                <div style={styles.row}>
                  {/* -------- LEFT COLUMN -------- */}
                  <div style={styles.col}>
                    {/* Step 1: Choose your Membership */}
                    <div style={styles.section}>
                      <label style={styles.label}>1. Choose your Membership</label>
                      <div style={styles.buttonGroup}>
                        {["basic", "pro", "enterprise"].map((type) => (
                          <motion.button
                            key={type}
                            type="button"
                            style={styles.btnChip(membershipType === type, buttonLoading[type])}
                            onClick={() => handleButtonClick(type, () => setMembershipType(type))}
                            whileHover={{ scale: buttonLoading[type] ? 1 : 1.05 }}
                            whileTap={{ scale: buttonLoading[type] ? 1 : 0.95 }}
                            disabled={buttonLoading[type]}
                          >
                            {buttonLoading[type] && <Loader size="12px" />}
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </motion.button>
                        ))}
                      </div>
                    </div>

                    {/* Step 2: Title of Membership */}
                    <div style={styles.section}>
                      <label style={styles.label}>2. Title of Membership</label>
                      <motion.input
                        style={styles.input}
                        placeholder="Beginner Cattle Management"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        whileFocus={{
                          borderColor: theme.primary,
                          boxShadow: `0 0 0 3px ${theme.primary}20`,
                        }}
                        required
                      />
                    </div>

                    {/* Step 3: Intended Learner */}
                    <div style={styles.section}>
                      <label style={styles.label}>3. Intended Learner</label>
                      <motion.input
                        style={styles.input}
                        placeholder="Farmers, livestock owners, agriculture students..."
                        value={intendedLearner}
                        onChange={(e) => setIntendedLearner(e.target.value)}
                        whileFocus={{
                          borderColor: theme.primary,
                          boxShadow: `0 0 0 3px ${theme.primary}20`,
                        }}
                        required
                      />
                    </div>

                    {/* Step 4: Select Courses */}
                    <div style={styles.section}>
                      <label style={styles.label}>4. Select Course Name</label>
                      <p style={{ fontSize: "0.8rem", color: theme.lightText, marginBottom: "1rem" }}>
                        We can select multiple courses which are present in system
                      </p>
                      
                      {selectedCourses.length > 0 && (
                        <div style={styles.selectedCourses}>
                          {selectedCourses.map(courseId => (
                            <div key={courseId} style={styles.selectedCourseTag}>
                              {getCourseName(courseId)}
                              <button
                                type="button"
                                style={styles.removeButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeSelectedCourse(courseId);
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="dropdown-container">
                        <button
                          type="button"
                          style={styles.dropdownButton}
                          onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                        >
                          <span>Select Courses ({selectedCourses.length} selected)</span>
                          <span>{showCourseDropdown ? "▲" : "▼"}</span>
                        </button>
                        
                        <AnimatePresence>
                          {showCourseDropdown && (
                            <motion.div
                              style={styles.dropdownContainer}
                              initial={{ opacity: 0, y: -10, height: 0 }}
                              animate={{ opacity: 1, y: 0, height: "auto" }}
                              exit={{ opacity: 0, y: -10, height: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <input
                                type="text"
                                placeholder="Search courses..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={styles.searchInput}
                                onClick={(e) => e.stopPropagation()}
                              />
                              
                              {filterCourses(availableCourses, searchQuery).map((course) => (
                                <div
                                  key={course.id}
                                  style={styles.courseCard(selectedCourses.includes(course.id))}
                                  onClick={() => toggleSelectCourse(course.id)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedCourses.includes(course.id)}
                                    readOnly
                                    style={{ 
                                      width: '1.25rem', 
                                      height: '1.25rem',
                                      accentColor: theme.accent,
                                      marginTop: '0.125rem'
                                    }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    <b style={{ color: theme.primary, fontSize: "1rem" }}>{course.name}</b>
                                    <div style={{ 
                                      fontSize: "0.875rem", 
                                      color: theme.lightText,
                                      marginTop: "0.25rem"
                                    }}>
                                      {course.description || "No description available"}
                                    </div>
                                    <div style={{ 
                                      fontSize: "0.75rem", 
                                      color: theme.accent,
                                      marginTop: "0.25rem",
                                      display: "flex",
                                      gap: "0.5rem",
                                      flexWrap: "wrap"
                                    }}>
                                      {course.categories.map((cat, index) => (
                                        <span key={index} style={{
                                          background: `${theme.accent}10`,
                                          padding: "0.25rem 0.5rem",
                                          borderRadius: "0.25rem"
                                        }}>
                                          {cat}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Step 5: Curriculum */}
                    <div style={styles.section}>
                      <label style={styles.label}>5. Curriculum</label>
                      <p style={{ fontSize: "0.8rem", color: theme.lightText, marginBottom: "1rem" }}>
                        Select courses for the curriculum under this membership
                      </p>
                      
                      {curriculumCourses.length > 0 && (
                        <div style={styles.selectedCourses}>
                          {curriculumCourses.map(courseId => (
                            <div key={courseId} style={styles.selectedCourseTag}>
                              {getCourseName(courseId)}
                              <button
                                type="button"
                                style={styles.removeButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeCurriculumCourse(courseId);
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="dropdown-container">
                        <button
                          type="button"
                          style={styles.dropdownButton}
                          onClick={() => setShowCurriculumDropdown(!showCurriculumDropdown)}
                        >
                          <span>Select Curriculum Courses ({curriculumCourses.length} selected)</span>
                          <span>{showCurriculumDropdown ? "▲" : "▼"}</span>
                        </button>
                        
                        <AnimatePresence>
                          {showCurriculumDropdown && (
                            <motion.div
                              style={styles.dropdownContainer}
                              initial={{ opacity: 0, y: -10, height: 0 }}
                              animate={{ opacity: 1, y: 0, height: "auto" }}
                              exit={{ opacity: 0, y: -10, height: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <input
                                type="text"
                                placeholder="Search courses for curriculum..."
                                value={curriculumSearchQuery}
                                onChange={(e) => setCurriculumSearchQuery(e.target.value)}
                                style={styles.searchInput}
                                onClick={(e) => e.stopPropagation()}
                              />
                              
                              {filterCourses(availableCourses, curriculumSearchQuery).map((course) => (
                                <div
                                  key={course.id}
                                  style={{
                                    ...styles.courseItem,
                                    background: curriculumCourses.includes(course.id) ? `${theme.primary}10` : "transparent"
                                  }}
                                  onClick={() => toggleCurriculumCourse(course.id)}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                                    <input
                                      type="checkbox"
                                      checked={curriculumCourses.includes(course.id)}
                                      readOnly
                                      style={{ 
                                        width: '1rem', 
                                        height: '1rem',
                                        accentColor: theme.primary
                                      }}
                                    />
                                    <div style={{ flex: 1 }}>
                                      <span style={{ 
                                        color: curriculumCourses.includes(course.id) ? theme.primary : theme.text,
                                        fontWeight: curriculumCourses.includes(course.id) ? "600" : "400",
                                        fontSize: "0.875rem"
                                      }}>
                                        {course.name}
                                      </span>
                                      <div style={{ 
                                        fontSize: "0.75rem", 
                                        color: theme.lightText,
                                        marginTop: "0.125rem"
                                      }}>
                                        {course.categories.join(", ")}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>

                  {/* -------- RIGHT COLUMN -------- */}
                  <div style={styles.col}>
                    {/* Step 6: Membership Access Plan */}
                    <div style={styles.section}>
                      <label style={styles.label}>6. Membership Access Plan</label>
                      <div style={styles.buttonGroup}>
                        {["free", "paid"].map((type) => (
                          <motion.button
                            key={type}
                            type="button"
                            style={styles.btnChip(accessType === type, buttonLoading[type])}
                            onClick={() => handleButtonClick(type, () => setAccessType(type))}
                            whileHover={{ scale: buttonLoading[type] ? 1 : 1.05 }}
                            whileTap={{ scale: buttonLoading[type] ? 1 : 0.95 }}
                            disabled={buttonLoading[type]}
                          >
                            {buttonLoading[type] && <Loader size="12px" />}
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </motion.button>
                        ))}
                      </div>
                      <AnimatePresence>
                        {accessType === "paid" && (
                          <motion.input
                            style={styles.input}
                            type="number"
                            placeholder="Amount in ₹"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            initial={{ opacity: 0, y: -10, height: 0, marginTop: 0 }}
                            animate={{ opacity: 1, y: 0, height: "auto", marginTop: "1rem" }}
                            exit={{ opacity: 0, y: -10, height: 0, marginTop: 0 }}
                            transition={{ duration: 0.3 }}
                            whileFocus={{
                              borderColor: theme.primary,
                              boxShadow: `0 0 0 3px ${theme.primary}20`,
                            }}
                            required
                          />
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Step 7: Membership Access Expiration */}
                    <div style={styles.section}>
                      <label style={styles.label}>7. Membership Access Expiration</label>
                      <div style={styles.buttonGroup}>
                        {["lifetime", "expiry"].map((type) => (
                          <motion.button
                            key={type}
                            type="button"
                            style={styles.btnChip(expiryType === type, buttonLoading[type])}
                            onClick={() => handleButtonClick(type, () => setExpiryType(type))}
                            whileHover={{ scale: buttonLoading[type] ? 1 : 1.05 }}
                            whileTap={{ scale: buttonLoading[type] ? 1 : 0.95 }}
                            disabled={buttonLoading[type]}
                          >
                            {buttonLoading[type] && <Loader size="12px" />}
                            {type === "lifetime" ? "Lifetime" : "Expiry"}
                          </motion.button>
                        ))}
                      </div>

                      <AnimatePresence>
                        {expiryType === "expiry" && (
                          <motion.div
                            initial={{ opacity: 0, y: -10, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: "auto" }}
                            exit={{ opacity: 0, y: -10, height: 0 }}
                            transition={{ duration: 0.3 }}
                            style={{ marginTop: '1rem' }}
                          >
                            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: '150px' }}>
                                <label style={{ ...styles.label, fontSize: '0.8rem' }}>Month</label>
                                <motion.input
                                  style={styles.input}
                                  type="number"
                                  placeholder="Months"
                                  value={expiryMonths}
                                  onChange={(e) => setExpiryMonths(e.target.value)}
                                  whileFocus={{
                                    borderColor: theme.primary,
                                    boxShadow: `0 0 0 3px ${theme.primary}20`,
                                  }}
                                />
                              </div>
                              <div style={{ flex: 1, minWidth: '150px' }}>
                                <label style={{ ...styles.label, fontSize: '0.8rem' }}>Year</label>
                                <motion.input
                                  style={styles.input}
                                  type="number"
                                  placeholder="Years"
                                  value={expiryYears}
                                  onChange={(e) => setExpiryYears(e.target.value)}
                                  whileFocus={{
                                    borderColor: theme.primary,
                                    boxShadow: `0 0 0 3px ${theme.primary}20`,
                                  }}
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Step 8: Membership Landing Page */}
                    <div style={styles.section}>
                      <label style={styles.label}>8. Membership Landing Page</label>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ ...styles.label, fontSize: '0.8rem' }}>Membership Title</label>
                        <div style={{
                          padding: '0.75rem',
                          background: `${theme.primary}10`,
                          borderRadius: '0.5rem',
                          color: theme.primary,
                          fontWeight: '600',
                          border: `1px solid ${theme.primary}20`
                        }}>
                          {title || "Membership title will appear here"}
                        </div>
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ ...styles.label, fontSize: '0.8rem' }}>Membership Description</label>
                        <motion.textarea
                          style={{ ...styles.textarea, height: '80px' }}
                          placeholder="Write a short summary about this membership..."
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          whileFocus={{
                            borderColor: theme.primary,
                            boxShadow: `0 0 0 3px ${theme.primary}20`,
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ ...styles.label, fontSize: '0.8rem' }}>Cover Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setCoverImageFile(e.target.files[0])}
                          style={{
                            border: `2px solid ${theme.border}`,
                            padding: '0.75rem',
                            borderRadius: '0.5rem',
                            width: '100%',
                            background: theme.cardBg,
                            fontSize: '0.875rem'
                          }}
                        />
                        <AnimatePresence>
                          {coverImagePreview && (
                            <motion.img
                              src={coverImagePreview}
                              alt="cover preview"
                              style={{
                                width: "100%",
                                maxWidth: "300px",
                                marginTop: "1rem",
                                borderRadius: "0.5rem",
                                border: `2px solid ${theme.border}`
                              }}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                            />
                          )}
                        </AnimatePresence>
                      </div>

                      <div>
                        <label style={{ ...styles.label, fontSize: '0.8rem' }}>Promo Video URL</label>
                        <motion.input
                          style={styles.input}
                          placeholder="https://youtube.com/embed/..."
                          value={promoVideoUrl}
                          onChange={(e) => setPromoVideoUrl(e.target.value)}
                          whileFocus={{
                            borderColor: theme.primary,
                            boxShadow: `0 0 0 3px ${theme.primary}20`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Step 9: Membership Message */}
                    <div style={styles.section}>
                      <label style={styles.label}>9. Membership Message</label>
                      <motion.textarea
                        style={styles.textarea}
                        placeholder="Welcome message for members..."
                        value={membershipMessage}
                        onChange={(e) => setMembershipMessage(e.target.value)}
                        whileFocus={{
                          borderColor: theme.primary,
                          boxShadow: `0 0 0 3px ${theme.primary}20`,
                        }}
                      />
                    </div>

                    {/* Step 10: Membership Launch */}
                    <div style={styles.section}>
                      <label style={styles.label}>10. Membership Launch</label>
                      <div style={styles.buttonGroup}>
                        {["published", "draft"].map((type) => (
                          <motion.button
                            key={type}
                            type="button"
                            style={styles.btnChip(status === type, buttonLoading[type])}
                            onClick={() => handleButtonClick(type, () => setStatus(type))}
                            whileHover={{ scale: buttonLoading[type] ? 1 : 1.05 }}
                            whileTap={{ scale: buttonLoading[type] ? 1 : 0.95 }}
                            disabled={buttonLoading[type]}
                          >
                            {buttonLoading[type] && <Loader size="12px" />}
                            {type === "published" ? "Publish" : "In draft"}
                          </motion.button>
                        ))}
                      </div>
                      <p style={{ 
                        fontSize: "0.75rem", 
                        color: theme.lightText, 
                        marginTop: "0.5rem",
                        fontStyle: "italic" 
                      }}>
                        {status === "published" 
                          ? "This membership will be visible to students" 
                          : "This membership will not be visible to students"}
                      </p>
                    </div>

                    {/* Submit Button */}
                    <div style={{ 
                      marginTop: "2rem", 
                      display: "flex", 
                      justifyContent: "center"
                    }}>
                      <motion.button
                        type="submit"
                        style={{
                          ...styles.submit,
                          position: "relative",
                          paddingLeft: loading ? "3rem" : "2rem"
                        }}
                        whileHover={{ 
                          scale: loading ? 1 : 1.05, 
                          boxShadow: loading 
                            ? "0 8px 25px rgba(109, 40, 217, 0.3)" 
                            : "0 12px 30px rgba(109, 40, 217, 0.4)" 
                        }}
                        whileTap={{ scale: loading ? 1 : 0.95 }}
                        disabled={loading}
                      >
                        {loading && (
                          <div style={styles.submitLoader} />
                        )}
                        {loading ? "Creating..." : "Create Membership"}
                      </motion.button>
                    </div>
                  </div>
                </div>
              </form>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Add CSS animation for loader */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .wrapper {
          display: flex;
          width: 100%;
          position: relative;
        }

        .main-content {
          position: relative;
        }

        .sidebar-collapsed {
          margin-left: 80px !important;
        }

        .sidebar-expanded {
          margin-left: 250px !important;
        }

        /* Custom Scrollbar Styles */
        .main-content::-webkit-scrollbar {
          width: 12px;
        }

        .main-content::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .main-content::-webkit-scrollbar-thumb {
          background: ${theme.primary};
          border-radius: 10px;
        }

        .main-content::-webkit-scrollbar-thumb:hover {
          background: ${theme.secondary};
        }
        
        /* For Firefox */
        .main-content {
          scrollbar-width: thin;
          scrollbar-color: ${theme.primary} #f1f1f1;
        }
      `}</style>
    </div>
  );
}